import { Tabs, Typography } from "antd"
import Main from "modules/layout/dashboard/main"
import { useAccidentId, useEventId, usePresenceId } from "pages/events"
import { useTranslation } from "react-i18next"
import { useExecutionMember } from "../../queries"
import { useEditAccident, useAccident } from "../queries"
import WarnedPerson from "./warned-person"
import Precision from "./precision"
import { boolean, object, string } from "yup"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { useEffect } from "react"
import Loader from "components/loader"

const { Title } = Typography

const AccidentDetail: React.FC = () => {
  const { t } = useTranslation()
  const eventId = useEventId()
  const presenceId = usePresenceId()
  const accidentId = useAccidentId()
  const member = useExecutionMember(eventId!, presenceId!)
  const accident = useAccident(eventId!, member.data?.member_id!, accidentId!, {
    enabled: member.isFetched,
  })
  const form = useEditAccidentForm()

  const { mutate, isLoading } = useEditAccident(
    eventId!,
    member.data?.member_id!,
    accidentId!,
    {
      meta: {
        entity: t("accident"),
        type: "edit",
      },
      onError(error: any) {
        if (error.response.data?.code === "form/validation") {
          for (let key in error.response.data.errors) {
            form.setError(key as any, {
              message: error.response.data.errors[key][0].message,
            })
          }
        }
      },
    }
  )

  const onSubmit = form.handleSubmit((data: IEditAccidentForm) => {
    mutate(data)
  })

  useEffect(() => {
    accident.data && form.reset(accident.data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accident.data])

  return (
    <>
      <Main.PageHeader
        title={<Title level={3}>{member.data?.member.name}</Title>}
      />
      <form style={{ width: "100%" }}>
        {accident.isLoading ? (
          <Loader />
        ) : (
          <Tabs
            items={[
              {
                key: "warned_person",
                label: t("accidents.warned_person"),
                children: (
                  <WarnedPerson
                    submitIsLoading={isLoading}
                    form={form}
                    onSubmit={onSubmit}
                  />
                ),
              },
              {
                key: "precision",
                label: t("accidents.precisions"),
                children: (
                  <Precision
                    submitIsLoading={isLoading}
                    form={form}
                    onSubmit={onSubmit}
                  />
                ),
              },
            ]}
          />
        )}
      </form>
    </>
  )
}

export default AccidentDetail

export interface IEditAccidentForm {
  emergency: boolean
  emergency_comment: string
  respondent_id?: string
  respondent_note: string
  nature: string
  situation: string
  intervention: string
  recommendation: string
  information: string
  accident_at: string
}

function useEditAccidentForm(defaultValues?: IEditAccidentForm) {
  const schema = object().shape({
    accident_at: string().nullable(),
    emergency: boolean().nullable(),
    emergency_services_note: string().nullable(),
    respondent_id: string().nullable(),
    respondent_note: string().nullable(),
    nature: string().nullable(),
    situation: string().nullable(),
    intervention: string().nullable(),
    recommendation: string().nullable(),
    information: string().nullable(),
  })
  return useForm<IEditAccidentForm>({
    resolver: yupResolver(schema),
    defaultValues,
  })
}
